<template>
  <div id="main-wrapper">
    <NavbarDark />
    <BannerOne v-if="stand" :key="stand.id" :item="stand?.gallery" />

    <section class="gray-simple">
      <div class="container">
        <div class="row">
          <div class="col-lg-8 col-md-12 col-sm-12">
            <div class="property_block_wrap style-2 p-4">
              <div class="prt-detail-title-desc">
                <span class="label text-light bg-success">{{ $t('Company Info') }}</span>
                <h3 class="mt-3">{{ stand?.name || $t('Stand Name') }}</h3>
                <span>{{ stand?.email || $t('Stand Email') }}</span>
              </div>
            </div>
            <PropertyDetail v-if="stand" :item="stand" :key="stand.id" :visitorCount="stand?.visitor_count"/>
          </div>

          <div class="col-lg-4 col-md-12 col-sm-12">
            <DetailSidebar v-if="stand" :item="stand" :key="stand.id" />
          </div>
        </div>
      </div>
    </section>

    <FooterTop :bg="'theme-bg'" />
    <FooterDark />
    <ScrollToTop />
  </div>
</template>

<script setup>
import { ref, onMounted, watch, computed } from 'vue';
import axios from 'axios';
import { useRoute, useRouter } from 'vue-router';

import NavbarDark from '@/components/navbar/navbar-dark.vue';
import BannerOne from '@/components/banner-one.vue';
import DetailSidebar from '@/components/property/detail-sidebar.vue';
import FooterTop from '@/components/footer/footer-top.vue';
import FooterDark from '@/components/footer/footer-dark.vue';
import ScrollToTop from '@/components/scroll-to-top.vue';
import PropertyDetail from '@/components/property/property-detail.vue';
import { useI18n } from "vue-i18n";
import { useHead } from '@vueuse/head'
const router = useRouter();

const stand = ref(null);
const route = useRoute();
const standSlug = computed(() => route.params.slug);

const { locale } = useI18n();
const initPage = async () => {
    await fetchStand();
    await trackVisitor();
};
let standRequestId = 0;

const fetchStand = async () => {
  const currentRequestId = ++standRequestId;

  try {
    const response = await axios.get(
      `/detail-of-stand/${standSlug.value}?lang=${locale.value}`
    );

    if (currentRequestId !== standRequestId) {
      return;
    }

    if (!response.data?.data?.id) {
      router.replace({ name: "NotFound" });
      return;
    }

    stand.value = response.data.data;

  } catch (error) {
    console.error("Error fetching hall:", error);
  }
};

const trackVisitor = async () => {

    if (!stand.value?.id) return;

    let visitorKey = localStorage.getItem('visitor_key');

    if (!visitorKey) {
        visitorKey = crypto.randomUUID();
        localStorage.setItem('visitor_key', visitorKey);
    }

    try {
        await axios.post(
            `/visitor/stand/${stand.value.id}`,
            {},
            {
                headers: { 'X-Visitor-Key': visitorKey }
            }
        );
    } catch (error) {
        console.error(error);
    }
};

///// SEO && Google
const baseUrl = 'https://ruqm.me'

const canonicalUrl = computed(() => {
  if (!stand.value?.slug?.[locale.value]) return ''
  return `${baseUrl}/${locale.value}/stand/${stand.value.slug[locale.value]}`
})

const hreflangLinks = computed(() => {
  if (!stand.value?.slug) return []

  return Object.entries(stand.value.slug).map(([lang, slug]) => ({
    rel: 'alternate',
    hreflang: lang,
    href: `${baseUrl}/${lang}/stand/${slug}`
  }))
})

const xDefault = computed(() => {
  const enSlug = stand.value?.slug?.en
  if (!enSlug) return null

  return {
    rel: 'alternate',
    hreflang: 'x-default',
    href: `${baseUrl}/en/stand/${enSlug}`
  }
})

useHead(() => {
  if (!stand.value?.id) return {}

  return {
    title: stand.value.seo_title || stand.value.name,
    meta: [
      {
        name: 'description',
        content: stand.value.seo_description || ''
      },
      {
        name: 'keywords',
        content: stand.value.focus_keywords?.join(', ') || ''
      },
      { property: 'og:type', content: 'event' },
      { property: 'og:url', content: canonicalUrl.value },
      { property: 'og:title', content: stand.value.seo_title },
      { property: 'og:description', content: stand.value.seo_description },
      { property: 'og:image', content: stand.value.seo_image },

      // Twitter
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: stand.value.seo_title },
      { name: 'twitter:description', content: stand.value.seo_description },
      { name: 'twitter:image', content: stand.value.seo_image },
    ],
    link: [
      { rel: 'canonical', href: canonicalUrl.value },
      ...hreflangLinks.value,
      ...(xDefault.value ? [xDefault.value] : [])
    ],
    script: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Event",
        "name": stand.value.name || 'Stand',
        "description": stand.value.description ,
        "image": stand.value.logo || [],
        "url": `https://ruqm.me${route.path}`,
        }),
        key: "schema"
      }
    ]
  }
})

watch(
    () => route.params.slug,
    async () => {
        await initPage();
    }
);
watch(locale, async (newLocale) => {

    if (!stand.value?.slug) return;

    const localizedSlug = stand.value.slug[newLocale];
    if (!localizedSlug) {
        await router.push({ name: 'NotFound' });
        return;
    }

    const newPath = `/${newLocale}/stand/${localizedSlug}`;

    if (route.fullPath !== newPath) {
        await router.replace(newPath);
    }

});

onMounted(async () => {
    await initPage();
});
</script>
<style scoped>
.tags-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.tag-item {
  background-color: #6A715D;
  color: #fff;
  padding: 0px 10px;
  border-radius: 12px;
  font-size: 0.9rem;
}
</style>